import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { Button } from "../GameObjects/Button";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";
import { PaintButton } from "../GameObjects/PaintButton";

export class PaintPalette extends PIXI.Container implements IOnboardingTarget {
    private static readonly SIZE: number = 5;
    private static readonly PAINT_SPACING: number = 100;
    private static readonly ARROW_SPACING: number = 120;

    private _colours!: number[];
    private _buttonPrev: Button;
    private _buttonNext: Button;
    private _buttonsPaint!: PaintButton[];
    private _buttonIndex: number;
    private _radioGroup: HLF.Input.RadioGroup<PaintButton>;
    private _activeColour: number | undefined;
    private _timeline: TimelineLite | undefined;

    constructor() {
        super();
        this._buttonIndex = 0;

        this._radioGroup = new HLF.Input.RadioGroup<PaintButton>();

        const arrowYPos: number =
            PaintPalette.PAINT_SPACING * ((PaintPalette.SIZE - 1) / 2) + PaintPalette.ARROW_SPACING;

        this._buttonPrev = new Button(
            this.onPrevButtonPressed.bind(this),
            "button-up", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this._buttonPrev.position.set(0, -arrowYPos);
        this.addChild(this._buttonPrev);

        this._buttonNext = new Button(
            this.onNextButtonPressed.bind(this),
            "button-down", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this._buttonNext.position.set(0, arrowYPos);
        this.addChild(this._buttonNext);

        this.setButtonEnabled(this._buttonPrev, false, true);
    }

    public get activeColour(): number | undefined {
        return this._activeColour;
    }

    public get buttonsPaint(): PaintButton[] {
        return this._buttonsPaint;
    }

    public get buttonsPaintActive(): PaintButton[] {
        return this._buttonsPaint.slice(this._buttonIndex, this._buttonIndex + PaintPalette.SIZE);
    }

    public get buttonPrev(): Button {
        return this._buttonPrev;
    }

    public get buttonNext(): Button {
        return this._buttonNext;
    }

    public setColours(pColours: number[]): void {
        this._colours = pColours;
        this._buttonsPaint = new Array<PaintButton>(this._colours.length);
        for (let i = 0; i < this._buttonsPaint.length; ++i) {
            const index: number = i;
            this._buttonsPaint[i] = new PaintButton(this._colours[i]);
            this._buttonsPaint[i].onSelected.push(() => { this.onPaintButtonSelected(index); });
            this._buttonsPaint[i].onDeselected.push(() => { this.onPaintButtonDeselected(index); });
            this._buttonsPaint[i].position.set(0, PaintPalette.PAINT_SPACING * (-((PaintPalette.SIZE - 1) / 2) + i));
            this._radioGroup.addElement(this._buttonsPaint[i]);
            if (i >= PaintPalette.SIZE) {
                this.setButtonEnabled(this._buttonsPaint[i], false, true);
            }
            this.addChild(this._buttonsPaint[i]);
        }
        HLF.PixiUtils.sendToFront(this._buttonPrev);
        HLF.PixiUtils.sendToFront(this._buttonNext);
    }

    public getOnboardingPosition(): PIXI.Point {
        return this._buttonsPaint[this._buttonIndex].getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return Math.PI * 0.5;
    }

    public onAnimateInComplete(): void {
        const buttons: HLF.Input.IFocusable[] = this.buttonsPaintActive;
        buttons.push(this._buttonPrev);
        buttons.push(this._buttonNext);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, buttons);
    }

    private onPaintButtonSelected(pIndex: number): void {
        this._activeColour = this._buttonsPaint[pIndex].colour;
        PubSub.publishSync(Topics.ACTIVE_PAINT_SELECTED, this._activeColour);
    }

    private onPaintButtonDeselected(pIndex: number): void {
        if (this._activeColour === this._buttonsPaint[pIndex].colour) {
            this._activeColour = undefined;
            PubSub.publishSync(Topics.ACTIVE_PAINT_DESELECTED, this._activeColour);
        }
    }

    private onPrevButtonPressed(): void {
        this.shiftPalette(-5);
    }

    private onNextButtonPressed(): void {
        this.shiftPalette(5);
    }

    private shiftPalette(pOffset: number): TimelineLite {
        if (this._timeline !== undefined) {
            this._timeline.kill();
        }
        // Disable navigation buttons while animations is happening
        this._buttonNext.interactive = false;
        this._buttonPrev.interactive = false;

        PubSub.publishSync(
            HLF.UNREGISTER_FOCUSABLES,
            this._buttonsPaint.slice(this._buttonIndex, this._buttonIndex +  PaintPalette.SIZE),
        );
        this._buttonIndex += pOffset;
        this._timeline = new TimelineLite();
        for (let i = 0; i < this._buttonsPaint.length; ++i) {
            this._timeline.to(this._buttonsPaint[i], 0.5, {
                y: PaintPalette.PAINT_SPACING * (-((PaintPalette.SIZE - 1) / 2) + i - this._buttonIndex),
                ease: Sine.easeInOut,
            }, 0);
            if (i < this._buttonIndex || i >= this._buttonIndex + PaintPalette.SIZE) {
                this._timeline.add(this.setButtonEnabled(this._buttonsPaint[i], false), 0);
            }
            else {
                this._timeline.add(this.setButtonEnabled(this._buttonsPaint[i], true), 0);
            }
        }

        this._timeline.call(() => {
            PubSub.publishSync(
                HLF.REGISTER_FOCUSABLES,
                this._buttonsPaint.slice(this._buttonIndex, this._buttonIndex + PaintPalette.SIZE),
            );
            this.setButtonEnabled(this._buttonPrev, this._buttonIndex > 0);
            this.setButtonEnabled(this._buttonNext, this._buttonIndex < this._colours.length - PaintPalette.SIZE);
            PubSub.publishSync(Topics.KEYBOARD_REMAP, undefined);
        });
        return this._timeline;
    }

    private setButtonEnabled(pButton: Button | PaintButton, pEnabled: boolean, pInstant: boolean = false): TweenLite {
        const button: Button | PaintButton = pButton;
        if (pEnabled) {
            return TweenLite.to(button, pInstant ? 0 : 0.25, {
                alpha: 1,
                ease: Sine.easeInOut,
                onComplete: () => {
                    button.interactive = true;
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLE, pButton);
                },
            });
        }
        else {
            button.interactive = false;
            // The Next + Prev buttons should always be navigable
            if (button !== this._buttonNext && button !== this._buttonPrev) {
                PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, pButton);
            }
            return TweenLite.to(button, pInstant ? 0 : 0.25, {
                alpha: (button instanceof PaintButton) ? 0 : 0.5,
                ease: Sine.easeInOut,
            });
        }
    }
}
